/**
 * Insights — the numbers. FR-407, FR-411.
 *
 * Numbers only in v0.3. Charts (FR-408) are a later release, and shipping them
 * before the underlying statistics are trusted would be decorating something
 * unverified.
 *
 * ## What this screen is careful about
 *
 * Cycles outside the plausible range are **counted, listed, and never
 * characterised** (FR-411). They appear under a neutral "set aside" heading with
 * the reason stated as a fact about the data — a gap longer than 90 days —
 * rather than as a fact about the person. "Your cycles are irregular" is a step
 * toward a clinical conclusion (L-08), and it lands badly on someone with PCOS
 * or endometriosis who already knows.
 */
import { en, t } from '@copy/index'
import { unusualCycles } from '@domain/index'
import { useEntries } from '@contexts/index'

const readable = (date: string) => t.date(new Date(`${date}T12:00:00`))

/** Day counts go through Intl.PluralRules so no locale renders "1 days". */
const days = (count: number) => t.plural(en.insights.days, count)

export function InsightsPage() {
  const { statistics, derived } = useEntries()
  const setAside = unusualCycles(derived)

  // One narrowed object, so there is nothing to assert non-null. See the
  // `cycleSummary` comment in domain/statistics.ts.
  const summary = statistics.cycleSummary

  return (
    <main className="flex flex-col gap-6 p-4">
      <h1 className="text-xl font-medium">{t('insights.title')}</h1>

      {summary === undefined ? (
        <p className="text-sm text-ink-muted">{t('insights.empty')}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3">
            <Stat label={t('insights.typicalCycle')} value={days(summary.typicalLength)} />
            {/* Label and value change together: "Usually within ±2 days" reads
                correctly, "Usually within None so far" does not. */}
            <Stat
              label={
                summary.variation === 0
                  ? t('insights.variationNone')
                  : t('insights.variation')
              }
              value={
                summary.variation === 0
                  ? t('insights.noVariation')
                  : t.plural(en.insights.plusMinusDays, summary.variation)
              }
            />
            <Stat label={t('insights.shortest')} value={days(summary.shortest)} />
            <Stat label={t('insights.longest')} value={days(summary.longest)} />
            {statistics.typicalPeriodLength !== undefined ? (
              <Stat
                label={t('insights.typicalPeriod')}
                value={days(statistics.typicalPeriodLength)}
              />
            ) : null}
            <Stat
              label={t('insights.cyclesRecorded')}
              value={String(statistics.cyclesUsed)}
            />
            <Stat label={t('insights.daysLogged')} value={String(statistics.daysLogged)} />
          </dl>

          <p className="text-xs text-ink-muted">{t('insights.medianNote')}</p>
        </>
      )}

      {setAside.length > 0 ? (
        <section className="border-t border-line pt-4">
          <h2 className="text-sm font-medium">{t('insights.setAside.heading')}</h2>
          <p className="pt-1 text-xs text-ink-muted">{t('insights.setAside.body')}</p>
          <ul className="pt-2">
            {setAside.map((cycle) => (
              <li key={cycle.start} className="py-0.5 text-sm text-ink-muted">
                {t.plural(en.insights.setAside.item, cycle.length, {
                  from: readable(cycle.start),
                  to: readable(cycle.end),
                  days: cycle.length,
                })}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="border-t border-line pt-4 text-xs text-ink-muted">
        {t('disclaimer.short')}
      </p>
    </main>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line p-3">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="pt-0.5 text-base">{value}</dd>
    </div>
  )
}
